import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { AssessmentPreviewModal } from './AssessmentPreviewModal';
import type { AssessmentPreviewTarget } from './AssessmentPreviewFlow';

interface AssessmentPreviewContextValue {
  openTemplatePreview: (templateId: string) => void;
  openCampaignPreview: (campaignId: string) => void;
}

const AssessmentPreviewContext = createContext<AssessmentPreviewContextValue | null>(null);

export const AssessmentPreviewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [target, setTarget] = useState<AssessmentPreviewTarget | null>(null);

  const openTemplatePreview = useCallback((templateId: string) => {
    setTarget({ kind: 'template', templateId });
  }, []);

  const openCampaignPreview = useCallback((campaignId: string) => {
    setTarget({ kind: 'campaign', campaignId });
  }, []);

  const value = useMemo(
    () => ({ openTemplatePreview, openCampaignPreview }),
    [openTemplatePreview, openCampaignPreview]
  );

  return (
    <AssessmentPreviewContext.Provider value={value}>
      {children}
      {target && <AssessmentPreviewModal target={target} onClose={() => setTarget(null)} />}
    </AssessmentPreviewContext.Provider>
  );
};

export function useAssessmentPreview(): AssessmentPreviewContextValue {
  const ctx = useContext(AssessmentPreviewContext);
  if (!ctx) {
    throw new Error('useAssessmentPreview must be used within AssessmentPreviewProvider');
  }
  return ctx;
}
